import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { ActorRefFrom } from 'xstate';
import { interactionKeys, prefetchScreenPop } from '../features/interactions/api.js';
import { agentSnapshotQueryKey } from '../features/presence/api.js';
import { supervisionKeys } from '../features/supervision/api.js';
import type { agentSessionMachine } from '../session/agent.machine.js';
import { meQueryKey } from '../session/use-current-user.js';
import type { EventNotification } from './frames.js';
import type { SequenceOutcome } from './sequences.js';

/**
 * Which cache entries one notification touches.
 *
 * A notification carries no payload, so "patch the cache" is not on offer for most of them: what
 * the client knows is *which aggregate* moved, and the repair is to read that aggregate back
 * through `/v1`, which authorises every field. That is the design and not a shortfall — the payload
 * is deliberately absent from a channel that has no per-subscriber authorisation.
 *
 * The table is keyed by aggregate type because that is what the gateway can derive from a
 * notification. A feature that adds a query for an aggregate adds its key here, in one line, and
 * every event about that aggregate starts repairing it.
 */
const myWorkQueryKey: QueryKey = interactionKeys.mine;

export function queryKeysFor(notification: EventNotification): QueryKey[] {
  const keys: QueryKey[] = [];
  switch (notification.aggregateType) {
    case 'Agent':
      // The agent, and what they are holding: an offer settled for them is a call they have just
      // taken or just lost, and `mine` is what the screen reads to know which.
      keys.push(agentSnapshotQueryKey, myWorkQueryKey);
      break;
    case 'Interaction':
      // The call, and the agent's own list with it: a call going active, into wrap-up or over is
      // exactly what joins or leaves that list, and an event arriving in a second window must move
      // it there too rather than only in the tab that pressed the button.
      keys.push(['interactions', notification.aggregateId], myWorkQueryKey);
      break;
    case 'ChatSession':
      // A chat said something, or ended. The pane reads the transcript back through `/v1`, because
      // the notification carries no body — the same rule as everywhere else here.
      //
      // The **prefix** and not one chat's key: a pane is opened by interaction id and this event's
      // aggregate is the session, so the two do not match, and an agent has at most their chat
      // capacity of panes open. Invalidating three cached transcripts to repair one is cheaper
      // than a second lookup to learn which.
      keys.push(['chats']);
      break;
    case 'Queue':
      // The queue itself, and the supervisor's board, which is a query over every queue in scope
      // and so is not reached by a key naming one of them.
      keys.push(['queues', notification.aggregateId], supervisionKeys.queueBoard);
      break;
    case 'Team':
      keys.push(['teams', notification.aggregateId], supervisionKeys.agentBoard);
      break;
    case 'Configuration':
      // A configuration version changes the queues, dispositions and business hours every screen
      // reads, so the whole of it is stale at once — and so are the permissions, because a role
      // change bumps `roles_version` and the token behind these calls is about to be replaced.
      keys.push(['config'], meQueryKey);
      break;
    default:
      break;
  }
  // The interaction an event belongs to, where the interaction is not the aggregate itself: the
  // kernel's rule is that the ordering key is the interaction id wherever one exists, so a ticket
  // or conversation event repairs the call's screen without this file knowing what those are.
  if (notification.orderingKey !== notification.aggregateId) {
    keys.push(['interactions', notification.orderingKey]);
  }
  return keys;
}

/**
 * The event types that start and end an offer to this agent.
 *
 * Only the fact is taken from the event; the offer's contents come from the interaction's snapshot.
 *
 * The pair that matters is addressed to the **agent**, not to the interaction
 * ([ADR-0025](../../../docs/90-decisions/0025-an-offer-reaches-its-agent-by-address.md)):
 * `interaction.offered` arrives on `interaction:<id>`, which this client cannot be subscribed to
 * before it has heard of the call, so an offer would never start. `agent.offer_pending` arrives on
 * `agent:<id>`, which it always is.
 *
 * The interaction's own endings stay in the withdrawn set beside `agent.offer_settled`. Once an offer
 * is accepted the client *is* following that interaction, and an offer to join a call that has ended
 * is not an offer — whichever event says so first wins, and both say the same thing.
 */
const offerStarted = 'agent.offer_pending';
const offerEnded: ReadonlySet<string> = new Set([
  'agent.offer_settled',
  'offer.expired',
  'offer.rejected',
  'interaction.active',
  'interaction.transferred',
  'interaction.wrapup',
  'interaction.completed',
  'interaction.abandoned',
  'interaction.cancelled',
  'interaction.failed',
]);

/**
 * Which interaction a notification is about.
 *
 * The agent-addressed pair has the **agent** as its aggregate, so the interaction is not the
 * aggregate id — and the payload is not published, so it is not in the event either. What carries it
 * is the ordering key, which the kernel's rule makes the interaction id wherever one exists.
 *
 * An interaction's own events have it as both, so the same read works for every case and there is no
 * branch here to get wrong.
 */
function interactionIdOf(notification: EventNotification): string {
  return notification.orderingKey;
}

export interface EventApplierDeps {
  readonly queryClient: QueryClient;
  readonly agentSession: ActorRefFrom<typeof agentSessionMachine>;
}

/**
 * Turns a notification into the two things it can mean: a cache entry to repair, and a fact for
 * the agent's own session.
 *
 * A duplicate does neither. At-least-once delivery means the same event can arrive twice, and a
 * client that invalidated on every copy would refetch as often as the relay retried; a client that
 * applied the offer twice would show two calls.
 */
export function createEventApplier(deps: EventApplierDeps) {
  return function applyEvent(notification: EventNotification, outcome: SequenceOutcome): void {
    if (outcome === 'duplicate') {
      return;
    }

    for (const queryKey of queryKeysFor(notification)) {
      void deps.queryClient.invalidateQueries({ queryKey });
    }

    if (notification.type === offerStarted) {
      // The screen pop starts here, while the phone is still ringing, rather than when the agent
      // accepts: LB-4 is met by asking earlier and not by a faster request.
      prefetchScreenPop(deps.queryClient, interactionIdOf(notification));
      deps.agentSession.send({
        type: 'offered',
        interactionId: interactionIdOf(notification),
        offeredAt: notification.occurredAt,
        sequence: notification.sequence,
      });
      return;
    }
    if (offerEnded.has(notification.type)) {
      deps.agentSession.send({
        type: 'offerWithdrawn',
        interactionId: interactionIdOf(notification),
      });
    }
  };
}
